import React, { useState, useEffect } from 'react';

// Reusable modal for selecting languages (strings)
const LanguageFilterModal = ({
  isOpen,
  onClose,
  onApply,
  initialSelectedLanguages = [],
  fetchLanguagesFunction, // Function to fetch the list of languages
  title // Title for the modal header
}) => {
  const [allLanguages, setAllLanguages] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]); // Store selected language strings
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch languages when the modal opens
  useEffect(() => {
    if (isOpen && fetchLanguagesFunction) {
      const fetchLangs = async () => {
        setIsLoading(true);
        setError(null);
        try {
          const response = await fetchLanguagesFunction();
          // Assuming the API returns an array of strings
          setAllLanguages(response || []);
          // Initialize selected languages based on the prop
          setSelectedLanguages(initialSelectedLanguages || []);
        } catch (err) {
          console.error(`Error fetching languages for ${title}:`, err);
          setError(`Failed to load languages.`);
          setAllLanguages([]);
        } finally {
          setIsLoading(false);
        }
      };
      fetchLangs();
    } else if (!isOpen) {
      // Reset state when modal closes
      setSearchTerm('');
      setError(null);
    }
  }, [isOpen, fetchLanguagesFunction, title, initialSelectedLanguages]); // Rerun if initial selection changes

  const handleCheckboxChange = (event, language) => {
    if (event.target.checked) {
      setSelectedLanguages(prev => [...prev, language]);
    } else {
      setSelectedLanguages(prev => prev.filter(lang => lang !== language));
    }
  };

  const handleRemoveLanguage = (language) => {
    setSelectedLanguages(prev => prev.filter(lang => lang !== language));
  };

  const handleApply = () => {
    onApply(selectedLanguages); // Pass back the array of selected language strings
    onClose();
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value.toLowerCase());
  };

  // Filter languages based on search term
  const filteredLanguages = allLanguages.filter(lang =>
    lang.toLowerCase().includes(searchTerm)
  );

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-40 flex justify-center items-center p-4">
      {/* Using max-w-2xl, adjust if needed */}
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold">{title || 'Filter by Language'}</h2>
        </div>

        {/* Selected Languages Display */}
        {selectedLanguages.length > 0 && (
          <div className="p-3 border-b border-gray-200 bg-gray-50">
            <span className="text-sm font-medium text-gray-600 mr-2">Selected:</span>
            <div className="flex flex-wrap gap-1 mt-1">
              {selectedLanguages.map(lang => (
                <span key={lang} className="flex items-center bg-blue-100 text-blue-800 text-xs font-medium px-2 py-0.5 rounded-full">
                  {lang}
                  <button
                    onClick={() => handleRemoveLanguage(lang)}
                    className="ml-1.5 text-blue-600 hover:text-blue-800 focus:outline-none"
                    aria-label={`Remove ${lang}`}
                  >
                    &times;
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Search Input */}
        <div className="p-3 border-b border-gray-200">
          <input
            type="text"
            placeholder="Search languages..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
          />
        </div>

        {/* Language List - Using 5 columns like the genre modal */}
        <div className="p-4 overflow-y-auto flex-grow">
          {isLoading && <p className="text-gray-500 text-center">Loading languages...</p>}
          {error && <p className="text-red-500 text-center">{error}</p>}
          {!isLoading && !error && (
            <ul className="grid grid-cols-5 gap-x-8 gap-y-2">
              {filteredLanguages.length > 0 ? (
                filteredLanguages.map(lang => (
                  <li key={lang} className="flex items-start text-sm">
                    <input
                      type="checkbox"
                      id={`lang-${lang}`}
                      checked={selectedLanguages.includes(lang)}
                      onChange={(e) => handleCheckboxChange(e, lang)}
                      className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mr-2 mt-1 flex-shrink-0"
                    />
                    <label htmlFor={`lang-${lang}`} className="text-sm text-gray-700 cursor-pointer text-left">
                      {lang}
                    </label>
                  </li>
                ))
              ) : (
                <p className="text-gray-500 text-center text-sm col-span-5">No languages found matching "{searchTerm}".</p>
              )}
            </ul>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 flex justify-end space-x-2 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default LanguageFilterModal;
